import type { ThunkDispatch, UnknownAction } from '@reduxjs/toolkit';
import { useCallback, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { z } from 'zod';
import type { EmergencyCallsState } from '@rsos/reduxDataStore/src/features/emergencyCalls/emergencyCallsSlice';
import type { SinatraState } from '@rsos/sinatra';
import { transcriptionApi } from '../../JurisdictionView/EventPane/tabs/TranscriptTab/transcriptionSlice';
import type { TranscriptionsState } from '../../JurisdictionView/EventPane/tabs/TranscriptTab/transcriptionSlice/transcriptionSlice.types';

const CALL_DATA_EVENT_TYPE = 'call_data.event';
const CALL_ENDED_EVENT_TYPE = 'call_ended';

const callEndedWebSocketMessageSchema = z.object({
  type: z.literal(CALL_DATA_EVENT_TYPE),
  topic: z.string(),
  body: z.object({
    event: z.object({
      event_name: z.literal('call_status'),
      event_type: z.literal(CALL_ENDED_EVENT_TYPE),
    }),
    metadata: z.object({
      call_id: z.number(),
      application: z.string(),
      created_time: z.number(),
    }),
  }),
});

type TranscriptionThunkDispatch = ThunkDispatch<
  {
    transcriptions: TranscriptionsState;
    emergencyCalls: EmergencyCallsState;
    sinatra: SinatraState;
  },
  string,
  UnknownAction
>;

/**
 * RTK Query keeps a cache entry alive for as long as anything is subscribed to
 * it (`keepUnusedDataFor: 0` only evicts once every subscriber is gone). A
 * one-off fetch dispatched outside a hook must release its subscription when
 * it settles, or the caller's transcript stays pinned in the cache and later
 * opens of the call reuse it instead of refetching.
 */
const releaseWhenSettled = (result: unknown) => {
  const subscription = result as { unsubscribe?: () => void } | undefined;
  Promise.resolve(result)
    .catch(() => undefined)
    .finally(() => subscription?.unsubscribe?.());
};

/**
 * Custom hook that handles call ended events from the StreamWS websocket.
 * When a call_data.event with event_type: "call_ended" is received,
 * it triggers a fetch for the final transcription data including audio.
 *
 * This provides a more reliable way to detect call endings compared to
 * relying on the is_final flag in transcription summaries.
 */
const useHandleCallEndedEvents = () => {
  const StreamWS = window?.__RSOS?.streamWS?.instance;
  const dispatch: TranscriptionThunkDispatch = useDispatch();

  const handleCallEndedEvent = useCallback(
    (msg: Record<string, unknown>) => {
      const parsed = callEndedWebSocketMessageSchema.safeParse(msg);
      if (!parsed.success) return;

      const { topic } = parsed.data;
      const callerId = topic.split('tel://')[1];
      if (!callerId) {
        // eslint-disable-next-line no-console -- Log for sentry
        console.error('No callerId found in topic:', topic);
        return;
      }

      const result: unknown = dispatch(
        transcriptionApi.endpoints.fetchTranscriptions.initiate(callerId, {
          forceRefetch: true,
        }),
      );
      releaseWhenSettled(result);
    },
    [dispatch],
  );

  useEffect(() => {
    if (StreamWS) {
      StreamWS.addOnMessageCallback({
        handleCallEndedEvent: handleCallEndedEvent,
      });

      return () => {
        StreamWS.removeOnMessageCallback('handleCallEndedEvent');
      };
    }
  }, [StreamWS, handleCallEndedEvent]);

  return null;
};

export default useHandleCallEndedEvents;
